import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";

const LivestockTypeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [livestockTypes, setLivestockTypes] = useState([]);
  const [filteredLivestockTypes, setFilteredLivestockTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    LivestockTypeID: "",
    LivestockTypeName: "",
    LivestockTypeNameEN: "",
    Remark: "",
    Status: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // Check if user has permission to edit/add (OfficeType === 'สทช.')
  const hasEditPermission = profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสประเภทปศุสัตว์",
      selector: (row) => row.LivestockTypeID,
      sortable: true,
    },
    {
      name: "ชื่อประเภทปศุสัตว์",
      selector: (row) => row.LivestockTypeName,
      sortable: true,
    },
    {
      name: "ชื่อประเภทปศุสัตว์ (ภาษาอังกฤษ)",
      selector: (row) => row.LivestockTypeNameEN,
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) =>
        hasEditPermission ? (
          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="แก้ไข"
          />
        ) : (
          <IconButton.Edit
            icon={faEdit}
            onClick={() => handleEdit(row)}
            label="ราละเอียด"
          />
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchLivestockTypes = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.LivestockTypeList || [];
        setLivestockTypes(data);
        setFilteredLivestockTypes(data);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchLivestockTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, value);
  };

  const applyFilters = (search, status) => {
    let filtered = livestockTypes;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) =>
        searchTerms.every((term) =>
          [
            row.LivestockTypeID.toLowerCase(),
            row.LivestockTypeName.toLowerCase(),
            row.LivestockTypeNameEN.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        )
      );
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredLivestockTypes(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterStatus("");
    setFilteredLivestockTypes(livestockTypes);
  };

  const handleAddNew = () => {
    setFormData({
      LivestockTypeID: "",
      LivestockTypeName: "",
      LivestockTypeNameEN: "",
      Remark: "",
      Status: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Status: row.Status ? "1" : "0",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "1" : "0" });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "LivestockTypeID":
      case "LivestockTypeName":
      case "LivestockTypeNameEN":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.LivestockTypeID &&
      formData.LivestockTypeName &&
      formData.LivestockTypeNameEN &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateLivestockType"
        : "/createLivestockType";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Status: formData.Status === "1",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตประเภทปศุสัตว์สำเร็จ!" : "สร้างประเภทปศุสัตว์สำเร็จ!");
        setShowForm(false);
        fetchLivestockTypes();
      }
    } catch (error) {
      console.error("Error saving livestock type:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      LivestockTypeID: "",
      LivestockTypeName: "",
      LivestockTypeNameEN: "",
      Remark: "",
      Status: "1",
    });
    setErrors({});
  };

  const handleDelete = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการลบข้อมูล");
      return;
    }
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบประเภทปศุสัตว์นี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateLivestockType",
        data: {
          ...formData,
          Status: false,
          UpdatedBy: sessionId,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("ลบประเภทปศุสัตว์สำเร็จ!");
        setShowForm(false);
        fetchLivestockTypes();
      }
    } catch (error) {
      console.error("Error deleting livestock type:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลประเภทปศุสัตว์
            </Heading>
          </HStack>
        </Box>

        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลประเภทปศุสัตว์" : "เพิ่มข้อมูลประเภทปศุสัตว์"}
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.LivestockTypeID}
                  isReadOnly={!hasEditPermission}
                  isDisabled={isEditing && hasEditPermission}
                >
                  <FormLabel>รหัสประเภทปศุสัตว์</FormLabel>
                  <Input
                    name="LivestockTypeID"
                    value={formData.LivestockTypeID}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.LivestockTypeID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.LivestockTypeName}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>ชื่อประเภทปศุสัตว์</FormLabel>
                  <Input
                    name="LivestockTypeName"
                    value={formData.LivestockTypeName}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.LivestockTypeName}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.LivestockTypeNameEN}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel>ชื่อประเภทปศุสัตว์ (ภาษาอังกฤษ)</FormLabel>
                  <Input
                    name="LivestockTypeNameEN"
                    value={formData.LivestockTypeNameEN}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.LivestockTypeNameEN}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl isDisabled={!hasEditPermission}>
                  <FormLabel>สถานะ</FormLabel>
                  <Switch
                    name="Status"
                    isChecked={formData.Status === "1"}
                    onChange={handleStatusChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {hasEditPermission && isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              {hasEditPermission && (
                <IconButton.Save onClick={handleSave} isLoading={isLoading} />
              )}
            </HStack>
          </Box>
        )}

        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาประเภทปศุสัตว์..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          {hasEditPermission && (
            <IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" />
          )}
        </HStack>

        {isLoading ? (
          <Spinner size="md" />
        ) : livestockTypes.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลประเภทปศุสัตว์
          </Text>
        ) : filteredLivestockTypes.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredLivestockTypes}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default LivestockTypeManagement;